import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import { useCallback } from 'react';
import { type SearchableField } from '../../ISearchableField.tsx';
import { useApplyFilterValues } from '../hooks/useApplyFilterValues.ts';
import { FilterBarEditPopoverContent } from './FilterBarEditPopoverContent.tsx';

export interface IFilterBarAddPopoverEditStepProps {
  /** Campo selezionato nello step "pick". */
  field: SearchableField;
  /** Necessario per il dispatcher `AdvancedSearchField` nel popover edit. */
  fields: SearchableField[];
  /** Query corrente (parsata), sorgente dei valori iniziali dell'editor. */
  query: Record<string, any>;
  /** Schema dei filtri, propagato al `FormHandler` annidato del popover. */
  schema: YupAnyObjectSchema;
  /** Conferma del nuovo filtro: scatta su "Applica". */
  onAdded: (name: string) => void;
  /** Chiude il popover. */
  onClose: () => void;
  /** Torna allo step "pick". */
  onBack: () => void;
}

/**
 * Step "edit" del popover di aggiunta filtro: monta l'editor del campo scelto.
 * Vive SOPRA il `FormHandler` annidato di `FilterBarEditPopoverContent`: per
 * questo `useApplyFilterValues` (che legge i context del form) risolve qui sul
 * form ESTERNO, quello corretto da valorizzare all'apply.
 */
export function FilterBarAddPopoverEditStep({
  field,
  fields,
  query,
  schema,
  onAdded,
  onClose,
  onBack,
}: Readonly<IFilterBarAddPopoverEditStepProps>) {
  const applyValues = useApplyFilterValues(field);

  // L'editor chiama un'unica callback: applichiamo i valori al form esterno,
  // confermiamo il nuovo filtro (`onAdded`) e chiudiamo il popover.
  const handleApplied = useCallback(
    (values: Record<string, unknown>) => {
      applyValues(values);
      onAdded(field.name);
      onClose();
    },
    [applyValues, onAdded, onClose, field.name],
  );

  return (
    <FilterBarEditPopoverContent
      field={field}
      fields={fields}
      query={query}
      schema={schema}
      onApplied={handleApplied}
      onCancel={onClose}
      onBack={onBack}
    />
  );
}
